import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ArrowLeft, Upload, X, Sparkles, ChevronRight, Pencil, Check, Loader2, Lightbulb, Target, Users, BookOpen, Settings2, Send, Paperclip, Palette } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { withBackend } from '../utils/api';
import { motion, AnimatePresence } from 'framer-motion';
import CreatePPTWorkspace from './CreatePPTWorkspace';
import InfographicWorkspace from './InfographicWorkspace';
import DocumentWorkspace from './DocumentWorkspace';
import DashboardWorkspace from './DashboardWorkspace';
import SheetsWorkspace from './SheetsWorkspace';

import useTypewriter from '../utils/useTypewriter';
import { getDefaultModel } from '../utils/models';
import { SHOW_SHEETS } from '../utils/features';
import { maybeConfirmCost } from '../utils/costConfirm';
import ReactMarkdown from 'react-markdown';

const OUTPUT_TYPE_META = {
  presentation: { label: 'Presentation', icon: '📊', color: 'green' },
  document: { label: 'Document', icon: '📄', color: 'blue' },
  infographic: { label: 'Infographic', icon: '🎨', color: 'purple' },
  dashboard: { label: 'Dashboard', icon: '📈', color: 'amber' },
  sheets: { label: 'Sheets', icon: '📋', color: 'teal' },
};

export default function PlanningWorkspace({ onBack, onComplete, onUpdate, onFullscreen, userId, existingProjectId, initialPrompt = '', initialFile = null }) {
  // ── Chat messages ──
  const [messages, setMessages] = useState([]);
  // ── Input ──
  const [input, setInput] = useState('');
  const initialPromptFiredRef = useRef(false);
  const [file, setFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [fileType, setFileType] = useState(null);
  // ── Phase tracking ──
  const [phase, setPhase] = useState('input'); // input | analyzing | questions | generating-plan | plan | brand-pick | outline | workspace
  // ── Data ──
  const [analysis, setAnalysis] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [plan, setPlan] = useState(null);
  const [outlineData, setOutlineData] = useState(null); // { title, slides }
  const [workspaceData, setWorkspaceData] = useState(null);
  // ── Model ──
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [modelOverride, setModelOverride] = useState(null);
  // ── Edit ──
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [editedPlan, setEditedPlan] = useState(null);
  const [isRefining, setIsRefining] = useState(false);
  // ── Outline ──
  const [outlineLoading, setOutlineLoading] = useState(false);
  // ── Variations ──
  const [variations, setVariations] = useState([]);
  const [selectedVariationIndex, setSelectedVariationIndex] = useState(null);
  const [variationSampleIndices, setVariationSampleIndices] = useState([]);
  const variationAbortRef = useRef(null);
  const variationSelectedRef = useRef(false);
  const [variationBrandMode, setVariationBrandMode] = useState(true);
  // ── Brand identities ──
  const [brandIdentities, setBrandIdentities] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(null);

  // Fetch user's brand identities on mount
  useEffect(() => {
    if (!userId) return;
    fetch(withBackend(`/api/brand-identities?user_id=${userId}`))
      .then(r => r.json())
      .then(data => setBrandIdentities(data.brand_identities || []))
      .catch(() => {});
  }, [userId]);

  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const workspaceRef = useRef(null);
  const promptRef = useRef('');
  const fileContentRef = useRef(null);
  const fileTypeRef = useRef(null);

  // Auto-scroll
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, phase]);

  // ─── File handling ───────────────────────────────────────────
  const [imageBase64, setImageBase64] = useState(null);
  const [imageMime, setImageMime] = useState(null);

  const [isFileParsing, setIsFileParsing] = useState(false);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    if (selectedFile.size > 20 * 1024 * 1024) { toast.error('File too large (max 20MB)'); return; }
    setFile(selectedFile);
    setIsFileParsing(true);

    const isImage = /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(selectedFile.name);

    if (isImage) {
      // For images: read as base64 for multimodal AI analysis (brand checking etc.)
      try {
        toast.loading('Reading image...', { id: 'file-parse' });
        const reader = new FileReader();
        reader.onload = () => {
          const b64 = reader.result.split(',')[1]; // strip data:...;base64, prefix
          setImageBase64(b64);
          setImageMime(selectedFile.type || 'image/png');
          setFileContent(`[Image: ${selectedFile.name}]`);
          setFileType('image');
          setIsFileParsing(false);
          toast.success('Image ready', { id: 'file-parse' });
        };
        reader.onerror = () => { setIsFileParsing(false); };
        reader.readAsDataURL(selectedFile);
      } catch (err) {
        toast.error('Failed to read image', { id: 'file-parse' });
        setFile(null);
        setIsFileParsing(false);
      }
    } else {
      // For documents: extract text
      const formData = new FormData();
      formData.append('file', selectedFile);
      // Request the FULL extracted text (no 5000-char truncation) and skip LLM
      // slide structuring — for AI Consultant we want the entire raw content
      // so the outline step can preserve every slide when user says "keep as is".
      formData.append('full_text', 'true');
      formData.append('structure', 'false');
      // For PDFs: use visual extraction (Gemini vision analyzes each page image)
      // so the LLM sees slide layout, hierarchy, charts — not just flat text
      const isPdf = selectedFile.name.toLowerCase().endsWith('.pdf');
      if (isPdf) formData.append('visual', 'true');
      try {
        const toastMsg = isPdf ? 'Analyzing PDF pages visually...' : 'Parsing file...';
        toast.loading(toastMsg, { id: 'file-parse' });
        const resp = await fetch(withBackend('/api/content/extract'), { method: 'POST', body: formData });
        if (!resp.ok) throw new Error('Failed to parse file');
        const result = await resp.json();
        setFileContent(result.extracted_text || result.text || '');
        setFileType(selectedFile.name.split('.').pop().toLowerCase());
        setImageBase64(null);
        setImageMime(null);
        setIsFileParsing(false);
        toast.success('File parsed', { id: 'file-parse' });
      } catch (err) {
        toast.error('Failed to parse file', { id: 'file-parse' });
        setFile(null);
        setIsFileParsing(false);
      }
    }
  };

  // ─── Stage 1+2: Analyze (streaming reasoning) ───────────────
  const handleAnalyze = async (userPrompt, { skipUserMessage = false, attachment = {} } = {}) => {
    promptRef.current = userPrompt;
    // Use attachment override if provided (from consultant flow where state was cleared),
    // otherwise fall back to component state (from direct submit)
    const effectiveFileContent = attachment?.fileContent || fileContent;
    const effectiveFileType = attachment?.fileType || fileType;
    const effectiveFileName = attachment?.file?.name || file?.name;

    // Always restore from attachment — the !file condition had a stale closure bug
    // where `file` still held the old value even after handleInputSubmit cleared it.
    // Also update refs so proceedToOutline (called much later) never gets stale values.
    if (attachment?.file) setFile(attachment.file);
    if (attachment?.fileContent) { setFileContent(attachment.fileContent); fileContentRef.current = attachment.fileContent; }
    if (attachment?.fileType) { setFileType(attachment.fileType); fileTypeRef.current = attachment.fileType; }

    setPhase('analyzing');
    setMessages(prev => [
      ...prev,
      ...(skipUserMessage ? [] : [{ role: 'user', text: userPrompt, fileName: effectiveFileName }]),
      { role: 'reasoning', text: '', streaming: true },
    ]);

    try {
      // Pass conversation history so the model can pick up context (e.g. audience, topic)
      const chatHistory = messages.filter(m => m.role === 'user' || m.role === 'bot').map(m => ({ role: m.role === 'bot' ? 'assistant' : 'user', text: m.text }));
      const resp = await fetch(withBackend('/api/plan/analyze-stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: userPrompt,
          file_content: effectiveFileContent,
          file_type: effectiveFileType,
          file_name: effectiveFileName,
          conversation_history: chatHistory,
          saved_brands: brandIdentities.map(b => ({ id: b.id, name: b.name, is_default: b.is_default })),
        }),
      });

      if (!resp.ok) {
        throw new Error('Analysis failed');
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let reasoningText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === 'reasoning_chunk') {
              reasoningText += event.text;
              setMessages(prev => prev.map(m =>
                m.streaming && m.role === 'reasoning'
                  ? { ...m, text: reasoningText }
                  : m
              ));
            }

            if (event.type === 'reasoning_done') {
              // Mark reasoning done, add a thinking indicator while structured analysis runs
              setMessages(prev => [
                ...prev.map(m =>
                  m.streaming && m.role === 'reasoning' ? { ...m, streaming: false } : m
                ),
                { role: 'thinking', text: 'Structuring analysis...', id: 'analysis-thinking' },
              ]);
            }

            if (event.type === 'result') {
              const result = event.data;

              // Remove the structuring indicator
              setMessages(prev => prev.filter(m => m.id !== 'analysis-thinking'));

              // Content offer: user signalled they're about to paste content
              if (result.awaiting_content) {
                const ack = result.chat_response || "Of course! Go ahead and paste your content — I'll analyze it and create the best output for you.";
                setMessages(prev => [...prev, { role: 'bot', text: ack }]);
                setPhase('input'); // Stay in input phase, awaiting the actual content
                return;
              }

              setAnalysis(result.inferred);

              if (result.skip_questions && result.plan) {
                setPlan(result.plan);
                setMessages(prev => [...prev, { role: 'plan', plan: result.plan, analysis: result.inferred }]);
                setPhase('plan');
              } else if (result.questions?.length > 0) {
                setQuestions(result.questions);
                setPhase('questions');
              } else {
                await generatePlan(result.inferred, []);
              }
            }

            if (event.type === 'error') {
              throw new Error(event.message);
            }
          } catch (e) {
            if (e.message && !e.message.includes('JSON')) throw e;
          }
        }
      }
    } catch (err) {
      setMessages(prev => prev.filter(m => !(m.role === 'reasoning' && m.streaming)));
      setMessages(prev => [...prev, { role: 'error', text: err.message || 'Analysis failed' }]);
      setPhase('input');
    }
  };

  // ─── Stage 3: Generate Plan (streaming) ─────────────────────
  const generatePlan = async (inferred, answersArr) => {
    setPhase('generating-plan');
    // Add a streaming reasoning bubble instead of a static thinking indicator
    setMessages(prev => [...prev, { role: 'reasoning', text: '', streaming: true, id: 'plan-reasoning' }]);

    try {
      const chatHistory = messages.filter(m => m.role === 'user' || m.role === 'bot').map(m => ({ role: m.role === 'bot' ? 'assistant' : 'user', text: m.text }));
      const resp = await fetch(withBackend('/api/plan/generate-stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: promptRef.current,
          file_content: fileContentRef.current || fileContent,
          file_type: fileTypeRef.current || fileType,
          inferred: inferred || analysis,
          answers: answersArr,
          conversation_history: chatHistory,
        }),
      });

      if (!resp.ok) throw new Error('Plan generation failed');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let reasoningText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === 'reasoning_chunk') {
              reasoningText += event.text;
              setMessages(prev => prev.map(m =>
                m.id === 'plan-reasoning' ? { ...m, text: reasoningText } : m
              ));
            }

            if (event.type === 'reasoning_done') {
              setMessages(prev => prev.map(m =>
                m.id === 'plan-reasoning' ? { ...m, streaming: false } : m
              ));
            }

            if (event.type === 'result') {
              const result = event.data;
              setPlan(result);
              setMessages(prev => [
                ...prev.filter(m => m.id !== 'plan-reasoning'),
                { role: 'reasoning', text: reasoningText },
                { role: 'plan', plan: result, analysis: inferred || analysis },
              ]);
              setPhase('plan');
            }

            if (event.type === 'error') throw new Error(event.message);
          } catch (e) {
            if (e.message && !e.message.includes('JSON')) throw e;
          }
        }
      }
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== 'plan-reasoning'));
      setMessages(prev => [...prev, { role: 'error', text: err.message || 'Plan generation failed' }]);
      setPhase('input');
    }
  };

  // ─── Question answer handler ─────────────────────────────────
  const handleQuestionAnswer = (answers) => {
    // Add user answers as a message
    const answerSummary = Object.values(answers).filter(Boolean).join(', ');
    setMessages(prev => [...prev, { role: 'user', text: answerSummary }]);
    setQuestions([]);

    const answersArr = questions.map(q => ({
      question_id: q.id,
      question: q.question,
      answer: answers[q.id] || '',
    })).filter(a => a.answer);

    generatePlan(analysis, answersArr);
  };

  // ─── Plan refinement ─────────────────────────────────────────
  const handleSaveEdits = async () => {
    if (!editedPlan) return;
    setIsRefining(true);
    try {
      const resp = await fetch(withBackend('/api/plan/refine'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          original_plan: plan,
          edited_plan: editedPlan,
          original_prompt: promptRef.current,
        }),
      });
      if (!resp.ok) throw new Error('Refinement failed');
      const result = await resp.json();
      setPlan(result);
      setEditedPlan(null);
      setIsEditingPlan(false);
      // Update the plan message in chat
      setMessages(prev => prev.map(m => m.role === 'plan' ? { ...m, plan: result } : m));
      toast.success('Plan updated');
    } catch (err) {
      toast.error(err.message || 'Failed to refine plan');
    } finally {
      setIsRefining(false);
    }
  };

  // ─── Approve & Generate Outline ──────────────────────────────
  const handleApprove = async () => {
    if (!plan) return;

    // Check brand identities before proceeding to outline
    const autoApplyBrand = brandIdentities.find(bi => bi.is_default && bi.auto_apply);

    // Also detect if the user explicitly named a brand in the conversation
    const userText = messages.filter(m => m.role === 'user').map(m => m.text || '').join(' ').toLowerCase();
    const mentionedBrand = !autoApplyBrand && brandIdentities.find(bi =>
      bi.name && userText.includes(bi.name.toLowerCase())
    );

    const brandToApply = autoApplyBrand || mentionedBrand;

    if (brandToApply) {
      // Auto-apply: set brand silently and proceed
      setSelectedBrandId(brandToApply.id);
      setMessages(prev => [...prev, {
        role: 'system',
        text: `Using brand: ${brandToApply.name}`,
        brandPill: { name: brandToApply.name, color: brandToApply.primary_color },
      }]);
    } else if (brandIdentities.length > 0 && !selectedBrandId) {
      // Has brands but no auto-apply → show picker
      setPhase('brand-pick');
      setMessages(prev => [...prev, {
        role: 'brand-picker',
        text: 'Would you like to apply a brand identity to this deck?',
        brands: brandIdentities,
      }]);
      return;
    }

    proceedToOutline();
  };

  const handleBrandSelect = (brandId) => {
    if (brandId === 'none') {
      setSelectedBrandId(null);
    } else {
      const brand = brandIdentities.find(bi => bi.id === brandId);
      setSelectedBrandId(brandId);
      if (brand) {
        setMessages(prev => [...prev, {
          role: 'system',
          text: `Using brand: ${brand.name}`,
          brandPill: { name: brand.name, color: brand.primary_color },
        }]);
      }
    }
    proceedToOutline();
  };

  const proceedToOutline = async () => {
    if (!plan) return;

    // Infer model
    let inferredModel = getDefaultModel();
    const complexity = analysis?.complexity || 'moderate';
    const register = analysis?.register || 'semi-formal';
    if (complexity === 'complex' || register === 'formal' || analysis?.content_source === 'existing') {
      inferredModel = 'opus';
    }
    const finalModel = modelOverride || inferredModel;
    const outputType = plan.recommended_output_type || 'presentation';

    setPhase('outline');
    setMessages(prev => [...prev, { role: 'reasoning', text: '', streaming: true, id: 'outline-thinking' }]);
    setOutlineLoading(true);

    const structure = plan.structure?.type_specific || {};
    // Use ref to avoid stale closure — fileContent state may be captured from an old render
    const currentFileContent = fileContentRef.current || fileContent;
    const currentFileType = fileTypeRef.current || fileType;
    // Send num_slides=0 so the outline LLM decides based on actual content volume.
    // The plan's slide_count is just a strategic recommendation — the outline LLM
    // counts sections/topics itself and should not be artificially capped.
    const defaultSlides = 0;

    try {
      const response = await fetch(withBackend('/api/generate-outline-stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: promptRef.current,
          num_slides: defaultSlides,
          language: 'en',
          text_style: 'balanced',
          tone: 'professional',
          llm_provider: 'gemini',
          audience: plan.audience?.who || null,
          project_type: outputType,
          file_content: currentFileContent || null,
          plan: plan,
          special_instructions: analysis?.special_instructions || '',
        }),
      });

      if (!response.ok) throw new Error('Failed to generate outline');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let allSlides = [];
      let title = '';
      let outlineMessageAdded = false;
      let thinkingText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === 'title' && event.title) {
              title = event.title;
            }

            if (event.type === 'thinking' && event.text) {
              thinkingText += event.text;
              setMessages(prev => prev.map(m =>
                m.id === 'outline-thinking' ? { ...m, text: thinkingText } : m
              ));
            }

            if (event.type === 'progress' && event.current && event.total) {
              // Once slides start, finalize the thinking bubble
              setMessages(prev => prev.map(m =>
                m.id === 'outline-thinking' ? { ...m, streaming: false } : m
              ));
            }

            if (event.type === 'slide') {
              allSlides = [...allSlides, event.slide];
              const outline = { title, slides: allSlides, streaming: true };
              setOutlineData(outline);

              if (!outlineMessageAdded) {
                outlineMessageAdded = true;
                // Finalize the thinking bubble and add outline
                setMessages(prev => {
                  const updated = prev.map(m =>
                    m.id === 'outline-thinking' ? { ...m, streaming: false } : m
                  );
                  updated.push({ role: 'outline', outline, outputType, id: 'outline-stream' });
                  return updated;
                });
              } else {
                setMessages(prev => prev.map(m =>
                  m.id === 'outline-stream' ? { ...m, outline } : m
                ));
              }
            }

            if (event.type === 'complete') {
              if (event.title) title = event.title;
              if (event.slides?.length) allSlides = event.slides;
              const outline = { title, slides: allSlides, streaming: false };
              setOutlineData(outline);
              setMessages(prev => prev.map(m =>
                m.id === 'outline-stream' ? { ...m, outline } : m
              ));
            }

            if (event.type === 'error') {
              // Backend sends the reason in event.error (not event.message).
              const err = new Error(event.error || event.message || 'Outline generation failed');
              err.isOutlineError = true;
              throw err;
            }
          } catch (e) {
            // Only surface deliberate outline errors — JSON.parse noise on
            // partial SSE lines stays swallowed.
            if (e.isOutlineError) throw e;
          }
        }
      }

      if (allSlides.length > 0) {
        const outline = { title, slides: allSlides, streaming: false };
        setOutlineData(outline);
        if (!outlineMessageAdded) {
          setMessages(prev => {
            const updated = prev.map(m =>
              m.id === 'outline-thinking' ? { ...m, streaming: false } : m
            );
            updated.push({ role: 'outline', outline, outputType, id: 'outline-stream' });
            return updated;
          });
        } else {
          setMessages(prev => prev.map(m =>
            m.id === 'outline-stream' ? { ...m, outline } : m
          ));
        }
        setPhase('outline-ready');
      }
    } catch (err) {
      setMessages(prev => {
        const updated = prev
          .map(m => m.id === 'outline-thinking' ? { ...m, streaming: false } : m)
          .filter(m => !(m.id === 'outline-thinking' && !m.text));
        updated.push({ role: 'error', text: `Outline generation failed: ${err.message}` });
        return updated;
      });
      setPhase('plan');
    } finally {
      setOutlineLoading(false);
    }
  };

  // ─── Start generation from approved outline ─────────────────
  const handleStartGeneration = async () => {
    if (!outlineData || !plan) return;

    let inferredModel = getDefaultModel();
    const complexity = analysis?.complexity || 'moderate';
    const register = analysis?.register || 'semi-formal';
    if (complexity === 'complex' || register === 'formal' || analysis?.content_source === 'existing') {
      inferredModel = 'opus';
    }
    const finalModel = modelOverride || inferredModel;
    const outputType = plan.recommended_output_type || 'presentation';

    if (!(await maybeConfirmCost({
      projectType: outputType,
      model: finalModel,
      items: outlineData.slides?.length || 1,
      userId,
    }))) return;

    const extractedInstructions = analysis?.special_instructions || '';

    const flowConfig = {
      projectType: outputType,
      generationMode: outputType === 'infographic' ? 'infographic'
        : outputType === 'document' ? 'document-html'
        : outputType === 'dashboard' ? 'dashboard-html'
        : 'html',
      htmlModel: finalModel,
      prompt: promptRef.current,
      language: 'en',
      themePreset: 'ai-create',
      includeImages: 'no',
      fileContent: fileContent || null,
      contentRelationship: null,
      brandIdentityId: selectedBrandId || null,
      specialInstructions: extractedInstructions,
    };

    // If a variation was chosen, include style reference and pre-generated slides
    if (selectedVariationIndex !== null && variations[selectedVariationIndex]) {
      const chosen = variations[selectedVariationIndex];
      flowConfig.variationStyleReference = chosen.slides[0]?.html_source || '';
      flowConfig.variationPreGeneratedSlides = chosen.slides;
      flowConfig.variationSampleIndices = variationSampleIndices;
      flowConfig.variationDirection = chosen.direction;
    }

    setWorkspaceData({ outline: outlineData, questionnaireData: { specialInstructions: extractedInstructions }, flowConfig });
    setMessages(prev => [...prev, { role: 'system', text: `Generating your ${OUTPUT_TYPE_META[outputType]?.label || 'content'}...` }]);
    setPhase('workspace');
  };

  // ─── Start variation generation (SSE stream) ──────────────────
  const startVariationGeneration = async (styleHint = '') => {
    if (!outlineData?.slides?.length) return;
    setPhase('generating-variations');
    setVariations([]);
    setSelectedVariationIndex(null);
    variationSelectedRef.current = false;
    const abortController = new AbortController();
    variationAbortRef.current = abortController;
    setMessages(prev => [...prev, { role: 'system', text: 'Generating design variations (3 sample slides each)...', isStatus: true }]);

    try {
      const response = await fetch(withBackend('/api/generate-variations-stream'), {
        method: 'POST',
        signal: abortController.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outline_slides: outlineData.slides.map(s => ({
            title: s.title || '',
            content: s.content || '',
            bullet_points: s.bullet_points || s.bulletPoints || [],
            slide_type: s.slide_type || s.slideType || '',
          })),
          variation_count: 3,
          presentation_title: outlineData.title || 'Presentation',
          visual_style: plan?.visual_direction?.style || 'professional',
          html_model: modelOverride || getDefaultModel(),
          include_images: 'no',
          user_prompt: promptRef.current,
          special_instructions: styleHint || '',
          brand_identity_id: variationBrandMode && selectedBrandId ? selectedBrandId : null,
          user_id: userId || '',
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            switch (event.type) {
              case 'start':
                setVariationSampleIndices(event.sample_indices || []);
                break;
              case 'direction':
                setVariations(prev => {
                  const updated = [...prev];
                  while (updated.length <= event.variation_index) updated.push({ direction: null, slides: [] });
                  updated[event.variation_index] = { direction: event.direction, slides: [] };
                  return updated;
                });
                break;
              case 'variation_slide':
                setVariations(prev => {
                  const updated = [...prev];
                  if (updated[event.variation_index]) {
                    const v = { ...updated[event.variation_index], slides: [...updated[event.variation_index].slides] };
                    while (v.slides.length <= event.slide_index) v.slides.push(null);
                    v.slides[event.slide_index] = { html_source: event.html_source };
                    updated[event.variation_index] = v;
                  }
                  return updated;
                });
                break;
              case 'variation_complete':
                setMessages(prev => [...prev, { role: 'system', text: `Variation ${event.variation_index + 1} ready.`, isStatus: true }]);
                break;
              case 'complete':
                if (!variationSelectedRef.current) setPhase('choosing-variation');
                break;
              case 'error':
                setMessages(prev => [...prev, { role: 'system', text: `Variation error: ${event.message}`, isError: true }]);
                break;
            }
          } catch {}
        }
      }
      if (!variationSelectedRef.current && variations.length === 0) setPhase('outline-ready');
    } catch (err) {
      if (err.name === 'AbortError') return;
      setMessages(prev => [...prev, { role: 'system', text: `Variation generation failed: ${err.message}`, isError: true }]);
      setPhase('outline-ready');
    }
  };

  const handleVariationSelect = (index) => {
    variationSelectedRef.current = true;
    if (variationAbortRef.current) {
      variationAbortRef.current.abort();
      variationAbortRef.current = null;
    }
    setSelectedVariationIndex(index);
  };

  // ─── Chat-based plan/outline editing ──────────────────────────
  const handlePlanEdit = async (editText) => {
    setMessages(prev => [...prev, { role: 'user', text: editText }]);
    setMessages(prev => [...prev, { role: 'thinking', text: 'Updating plan...' }]);

    try {
      // Create an edited plan with the user's instruction applied via refine
      const editedVersion = JSON.parse(JSON.stringify(plan));
      // Pass the edit as a text instruction — the refine endpoint will reconcile
      const resp = await fetch(withBackend('/api/plan/refine'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          original_plan: plan,
          edited_plan: { ...editedVersion, _user_edit_instruction: editText },
          original_prompt: promptRef.current,
        }),
      });
      if (!resp.ok) throw new Error('Plan update failed');
      const result = await resp.json();
      setPlan(result);

      setMessages(prev => {
        const updated = prev.filter(m => m.role !== 'thinking');
        if (result.reasoning) {
          updated.push({ role: 'reasoning', text: result.reasoning });
        }
        // Update the existing plan card
        return updated.map(m => m.role === 'plan' ? { ...m, plan: result } : m);
      });
    } catch (err) {
      setMessages(prev => {
        const updated = prev.filter(m => m.role !== 'thinking');
        updated.push({ role: 'error', text: err.message });
        return updated;
      });
    }
  };

  // ── AI Consultant general chat — routes between create and inquiry ──
  const handleConsultantMessage = async (text, attachment = {}) => {
    const userMsg = { role: 'user', text };
    if (attachment.file) userMsg.fileName = attachment.file.name;
    setMessages(prev => [...prev, userMsg]);
    try {
      // Classify: does the user want to create something or just chat?
      const recentChat = messages.filter(m => m.role === 'user' || m.role === 'bot').map(m => ({ role: m.role, text: m.text }));
      const routeResp = await fetch(withBackend('/api/chat/route'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          workspace_type: 'consultant',
          content_summary: 'AI Consultant — general assistant',
          conversation_history: recentChat,
        }),
      });
      const { intent } = await routeResp.json();

      if (intent === 'create') {
        // User wants to create something — enter the planning pipeline
        // Pass attachment directly since file state was cleared by handleInputSubmit
        handleAnalyze(text, { skipUserMessage: true, attachment });
      } else {
        // General question — stream from consultant chat endpoint
        setMessages(prev => [...prev, { role: 'bot', text: '', isStreaming: true }]);
        const recentHistory = messages.filter(m => m.role === 'user' || m.role === 'bot');
        const payload = {
          message: text,
          user_id: userId,
          history: recentHistory,
        };
        if (attachment.fileContent) {
          payload.file_content = attachment.fileContent;
          payload.file_name = attachment.file?.name;
          payload.file_type = attachment.fileType;
        }
        // Pass image data for multimodal analysis (brand checking etc.)
        if (attachment.imageBase64 || imageBase64) {
          payload.image_data = attachment.imageBase64 || imageBase64;
          payload.image_mime = attachment.imageMime || imageMime || 'image/png';
        }
        const resp = await fetch(withBackend('/api/consultant/chat'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';
        let thinkingText = '';
        let isThinking = false;
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'thinking_start') {
                isThinking = true;
                thinkingText = '';
                // Insert a reasoning message before the streaming bot message
                setMessages(prev => {
                  const updated = [...prev];
                  const streamIdx = updated.findLastIndex(m => m.isStreaming);
                  if (streamIdx >= 0) {
                    updated.splice(streamIdx, 0, { role: 'reasoning', text: '', streaming: true });
                  }
                  return updated;
                });
              } else if (event.type === 'thinking') {
                thinkingText += event.content;
                setMessages(prev => {
                  const updated = [...prev];
                  const reasoningIdx = updated.findLastIndex(m => m.role === 'reasoning' && m.streaming);
                  if (reasoningIdx >= 0) {
                    updated[reasoningIdx] = { ...updated[reasoningIdx], text: thinkingText };
                  }
                  return updated;
                });
              } else if (event.type === 'thinking_end') {
                isThinking = false;
                // Finalize reasoning message
                setMessages(prev => {
                  const updated = [...prev];
                  const reasoningIdx = updated.findLastIndex(m => m.role === 'reasoning' && m.streaming);
                  if (reasoningIdx >= 0) {
                    updated[reasoningIdx] = { role: 'reasoning', text: thinkingText };
                  }
                  return updated;
                });
              } else if (event.type === 'text') {
                accumulated += event.content;
                setMessages(prev => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.isStreaming) {
                    updated[updated.length - 1] = { ...last, text: accumulated };
                  }
                  return updated;
                });
              } else if (event.type === 'done') {
                setMessages(prev => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.isStreaming) {
                    updated[updated.length - 1] = { role: 'bot', text: accumulated || 'I couldn\'t generate a response.' };
                  }
                  return updated;
                });
              }
            } catch {}
          }
        }
        // Finalize if stream ended without 'done' event
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.isStreaming) {
            updated[updated.length - 1] = { role: 'bot', text: accumulated || 'I couldn\'t generate a response.' };
          }
          return updated;
        });
        setPhase('chat'); // Stay in chat mode for follow-ups
      }
    } catch (err) {
      console.error('[handleConsultantMessage] error:', err);
      // Fallback: treat as creation request (skip user message since already added)
      handleAnalyze(text, { skipUserMessage: true });
    }
  };

  // ── Smart workspace chat — edit vs inquiry routing ──
  const handleWorkspaceChat = async (text) => {
    setMessages(prev => [...prev, { role: 'user', text }]);
    try {
      const routeResp = await fetch(withBackend('/api/chat/route'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          workspace_type: workspaceData?.type || 'presentation',
          content_summary: outlineData ? `${outlineData.title} — ${outlineData.slides?.length || 0} slides` : '',
        }),
      });
      const { intent } = await routeResp.json();

      if (intent === 'edit') {
        setMessages(prev => [...prev, { role: 'bot', text: 'Applying edit...' }]);
        if (workspaceRef.current?.sendEdit) {
          workspaceRef.current.sendEdit(text);
        } else if (workspaceRef.current?.handleChatMessage) {
          workspaceRef.current.handleChatMessage(text);
        }
      } else {
        setMessages(prev => [...prev, { role: 'bot', text: '...', isLoading: true }]);
        const contentContext = (outlineData?.slides || []).map((s, i) => `${i + 1}. ${s.title}: ${s.content || ''}`).join('\n').slice(0, 20000);
        const recentHistory = messages.filter(m => m.role === 'user' || m.role === 'bot');
        const inquiryResp = await fetch(withBackend('/api/chat/inquiry'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: text,
            workspace_type: workspaceData?.type || 'presentation',
            content_context: contentContext,
            chat_history: recentHistory,
            user_id: userId || '',
          }),
        });
        const { response } = await inquiryResp.json();
        setMessages(prev => {
          const filtered = prev.filter(m => !m.isLoading);
          return [...filtered, { role: 'bot', text: response || 'I couldn\'t generate a response.' }];
        });
      }
    } catch (err) {
      // Fallback to direct edit
      if (workspaceRef.current?.sendEdit) {
        workspaceRef.current.sendEdit(text);
      } else if (workspaceRef.current?.handleChatMessage) {
        workspaceRef.current.handleChatMessage(text);
      }
    }
  };

  // ── Plan phase message routing — classify before refining ──
  const handlePlanPhaseMessage = async (text) => {
    setMessages(prev => [...prev, { role: 'user', text }]);
    try {
      // Use the consultant intent router to decide: is this a plan edit or a conversation?
      const routeResp = await fetch(withBackend('/api/chat/route'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, workspace_type: 'consultant' }),
      });
      const { intent } = await routeResp.json();

      if (intent === 'create') {
        // User wants to edit/refine the plan
        setMessages(prev => [...prev, { role: 'thinking', text: 'Updating plan...' }]);
        const editedVersion = JSON.parse(JSON.stringify(plan));
        const resp = await fetch(withBackend('/api/plan/refine'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            original_plan: plan,
            edited_plan: { ...editedVersion, _user_edit_instruction: text },
            original_prompt: promptRef.current,
          }),
        });
        if (!resp.ok) throw new Error('Plan update failed');
        const result = await resp.json();
        setPlan(result);
        setMessages(prev => {
          const updated = prev.filter(m => m.role !== 'thinking');
          if (result.reasoning) updated.push({ role: 'reasoning', text: result.reasoning });
          return updated.map(m => m.role === 'plan' ? { ...m, plan: result } : m);
        });
      } else {
        // User is asking a question or correcting — route to consultant chat
        setMessages(prev => [...prev, { role: 'bot', text: '...', isLoading: true }]);
        const recentHistory = messages.filter(m => m.role === 'user' || m.role === 'bot');
        const resp = await fetch(withBackend('/api/consultant/chat'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: text, user_id: userId, history: recentHistory }),
        });
        // Stream the response
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          for (const line of chunk.split('\n')) {
            if (!line.startsWith('data: ')) continue;
            try {
              const payload = JSON.parse(line.slice(6));
              if (payload.type === 'text') {
                accumulated += payload.content;
                setMessages(prev => {
                  const filtered = prev.filter(m => !m.isLoading);
                  return [...filtered, { role: 'bot', text: accumulated }];
                });
              }
            } catch {}
          }
        }
        if (!accumulated) {
          setMessages(prev => {
            const filtered = prev.filter(m => !m.isLoading);
            return [...filtered, { role: 'bot', text: 'I couldn\'t generate a response.' }];
          });
        }
      }
    } catch (err) {
      // Fallback: show error
      setMessages(prev => {
        const filtered = prev.filter(m => m.role !== 'thinking' && !m.isLoading);
        return [...filtered, { role: 'error', text: err.message || 'Something went wrong' }];
      });
    }
  };

  // ─── Chat input submit ───────────────────────────────────────
  const handleInputSubmit = () => {
    if (isFileParsing) {
      toast.error('Still analyzing your file — please wait a moment.');
      return;
    }
    const text = input.trim();
    if (!text) return;
    const attachedFile = file;
    const attachedContent = fileContent;
    const attachedType = fileType;
    const attachedImageB64 = imageBase64;
    const attachedImageMime = imageMime;
    setInput('');
    setFile(null);
    setFileContent(null);
    setFileType(null);
    setImageBase64(null);
    setImageMime(null);

    if (phase === 'input' || phase === 'chat') {
      handleConsultantMessage(text, { file: attachedFile, fileContent: attachedContent, fileType: attachedType, imageBase64: attachedImageB64, imageMime: attachedImageMime });
    } else if (phase === 'choosing-variation' || phase === 'generating-variations') {
      // User wants different variations — re-generate with their style hint
      setMessages(prev => [...prev, { role: 'user', text }]);
      startVariationGeneration(text);
    } else if (phase === 'plan' || phase === 'outline-ready') {
      // Route through intent classification — correction/inquiry goes to chat, edits refine the plan
      handlePlanPhaseMessage(text);
    } else if (phase === 'workspace') {
      // Smart routing: edit vs inquiry
      handleWorkspaceChat(text);
    }
  };

  // ── Process initial file from homepage ──
  useEffect(() => {
    if (initialFile && !file) {
      const dt = new DataTransfer();
      dt.items.add(initialFile);
      const syntheticEvent = { target: { files: dt.files } };
      handleFileSelect(syntheticEvent);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-submit initial prompt from homepage ──
  useEffect(() => {
    if (initialPrompt && !initialPromptFiredRef.current && phase === 'input') {
      initialPromptFiredRef.current = true;
      // Route through consultant message handler — delay more if file needs processing
      setTimeout(() => handleConsultantMessage(initialPrompt), initialFile ? 1500 : 200);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isFileParsing) handleInputSubmit();
    }
  };

  const isWorkspacePhase = phase === 'workspace';
  const showInput = phase === 'input' || phase === 'chat' || phase === 'plan' || phase === 'outline-ready' || phase === 'choosing-variation' || phase === 'generating-variations' || isWorkspacePhase;
  const isLoading = phase === 'analyzing' || phase === 'generating-plan' || outlineLoading;

  // ─── Render ──────────────────────────────────────────────────
  return (
    <div className="h-dvh flex flex-col bg-background">
      <div className="flex-1 flex overflow-hidden">
        {/* Chat panel */}
        <div className={`flex flex-col ${isWorkspacePhase ? 'w-[380px] border-r border-border/50' : 'flex-1'} transition-all duration-300`}>
          {/* Header */}
          <div className="flex-shrink-0 px-4 py-3 border-b border-border/50 flex items-center gap-3">
            <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <Sparkles className="w-5 h-5 text-accent" />
            <h1 className="text-sm font-semibold">AI Consultant</h1>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            {/* Welcome message if empty */}
            {messages.length === 0 && phase === 'input' && (
              <div className="max-w-2xl mx-auto text-center pt-16">
                <h2 className="text-2xl font-bold text-foreground mb-3">What do you want to create?</h2>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                  Describe your goal and I'll build a strategic plan — format, structure, and content strategy included.
                </p>
                <div className="flex flex-wrap justify-center gap-2 text-xs text-muted-foreground">
                  {[
                    '"6-slide deck about AI in Jordan"',
                    '"Pitch deck for my SaaS startup"',
                    '"Quarterly report for stakeholders"',
                    '"Infographic on market trends"',
                  ].map((example, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(example.replace(/"/g, ''))}
                      className="px-3 py-1.5 rounded-full border border-border/50 hover:border-accent/50 hover:bg-accent/5 transition-colors cursor-pointer"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <ChatMessage key={i} msg={msg} onBrandSelect={handleBrandSelect} />
            ))}

            {/* Plan card inline */}
            {messages.some(m => m.role === 'plan') && phase === 'plan' && plan && !isEditingPlan && (
              <div className="space-y-3 pb-4">
                {/* Advanced settings */}
                <div className="ml-1">
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <Settings2 className="w-3 h-3" />
                    Advanced
                    <ChevronRight className={`w-3 h-3 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
                  </button>
                  {showAdvanced && (
                    <div className="mt-2 flex items-center gap-2">
                      {[
                        { value: null, label: 'Auto' },
                        { value: 'flash', label: 'Flash' },
                        { value: 'opus', label: 'Opus' },
                      ].map(opt => (
                        <button
                          key={opt.label}
                          onClick={() => setModelOverride(opt.value)}
                          className={`px-2.5 py-1 rounded-full text-xs border transition-all ${
                            modelOverride === opt.value
                              ? 'bg-accent text-accent-foreground border-accent'
                              : 'bg-muted/50 border-border hover:border-accent/50 text-foreground'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-3">
                  <Button onClick={handleApprove} size="sm" className="px-5">
                    <Check className="w-3.5 h-3.5 mr-1.5" />
                    Approve & Generate
                  </Button>
                  <button
                    onClick={() => { setIsEditingPlan(true); setEditedPlan(JSON.parse(JSON.stringify(plan))); }}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="w-3 h-3" />
                    Edit
                  </button>
                </div>
              </div>
            )}

            {/* Plan editor */}
            {isEditingPlan && editedPlan && (
              <PlanEditor
                plan={editedPlan}
                onChange={setEditedPlan}
                onSave={handleSaveEdits}
                onCancel={() => { setIsEditingPlan(false); setEditedPlan(null); }}
                isRefining={isRefining}
              />
            )}

            {/* Outline approval buttons */}
            {phase === 'outline-ready' && outlineData && (
              <div className="space-y-3 pb-4">
                <div className="flex items-center gap-3">
                  <Button onClick={handleStartGeneration} size="sm" className="px-5">
                    <Check className="w-3.5 h-3.5 mr-1.5" />
                    Generate
                  </Button>
                  {(plan?.recommended_output_type || 'presentation') === 'presentation' && (
                    <Button onClick={() => startVariationGeneration()} size="sm" variant="outline" className="px-4">
                      <Palette className="w-3.5 h-3.5 mr-1.5" />
                      Design Variations
                    </Button>
                  )}
                  <span className="text-xs text-muted-foreground">or ask for changes below</span>
                </div>
              </div>
            )}

            {/* Variation picker */}
            {(phase === 'generating-variations' || phase === 'choosing-variation') && (
              <div className="space-y-4 pb-4">
                <div className="text-sm font-medium flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  {phase === 'generating-variations' ? 'Generating Design Variations...' : 'Choose Your Design Style'}
                </div>
                {selectedBrandId && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setVariationBrandMode(true); if (variations.length > 0) startVariationGeneration(); }}
                      className={`px-3 py-1 text-xs rounded-full border transition-colors ${variationBrandMode ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'}`}
                    >
                      On-brand layouts
                    </button>
                    <button
                      onClick={() => { setVariationBrandMode(false); if (variations.length > 0) startVariationGeneration(); }}
                      className={`px-3 py-1 text-xs rounded-full border transition-colors ${!variationBrandMode ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'}`}
                    >
                      Creative styles
                    </button>
                  </div>
                )}
                {variations.length === 0 && phase === 'generating-variations' && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" /> Creating variations...
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {variations.map((v, vi) => (
                    <div
                      key={vi}
                      onClick={() => handleVariationSelect(vi)}
                      className={`cursor-pointer rounded-lg border-2 p-3 transition-all hover:shadow-md ${selectedVariationIndex === vi ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                    >
                      {v.direction && (
                        <div className="mb-2">
                          <div className="text-sm font-medium">{v.direction.name}</div>
                          <div className="text-xs text-muted-foreground line-clamp-2">{v.direction.description}</div>
                          {v.direction.colors?.length > 0 && (
                            <div className="flex gap-1 mt-1.5">
                              {v.direction.colors.map((c, ci) => (
                                <div key={ci} className="w-5 h-5 rounded-full border border-border" style={{ backgroundColor: c }} />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {v.slides?.length > 0 && (
                        <div className="grid grid-cols-3 gap-1">
                          {v.slides.filter(Boolean).map((s, si) => (
                            <div key={si} className="aspect-video rounded overflow-hidden border border-border/50 bg-white">
                              <iframe
                                srcDoc={s.html_source}
                                className="w-full h-full pointer-events-none"
                                style={{ transform: 'scale(0.18)', transformOrigin: 'top left', width: '555%', height: '555%' }}
                                title={`Variation ${vi + 1} slide ${si + 1}`}
                                sandbox="allow-same-origin"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      {!v.slides?.length && <div className="flex items-center justify-center h-16 text-xs text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>}
                    </div>
                  ))}
                </div>
                {(phase === 'choosing-variation' || (phase === 'generating-variations' && selectedVariationIndex !== null)) && selectedVariationIndex !== null && (
                  <div className="flex items-center gap-3">
                    <Button onClick={handleStartGeneration} size="sm" className="px-5">
                      <Check className="w-3.5 h-3.5 mr-1.5" />
                      Generate with {variations[selectedVariationIndex]?.direction?.name || 'Selected Style'}
                    </Button>
                    <span className="text-xs text-muted-foreground">or type a style request below for different variations</span>
                  </div>
                )}
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Question overlay — Claude style */}
          <AnimatePresence>
            {phase === 'questions' && questions.length > 0 && (
              <QuestionOverlay
                questions={questions}
                onSubmit={handleQuestionAnswer}
                onSkip={() => { setQuestions([]); generatePlan(analysis, []); }}
              />
            )}
          </AnimatePresence>

          {/* Input area */}
          {showInput && (
            <div className="flex-shrink-0 px-6 pb-6 pt-2">
              <div className={`max-w-4xl ${!isWorkspacePhase ? 'mx-auto' : ''}`}>
                {file && (
                  <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-muted/50 rounded-lg text-sm">
                    {isFileParsing ? <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" /> : <Paperclip className="w-4 h-4 text-muted-foreground" />}
                    <span className="text-muted-foreground truncate flex-1">{isFileParsing ? `Parsing ${file.name}...` : file.name}</span>
                    {!isFileParsing && (
                      <button onClick={() => { setFile(null); setFileContent(null); setFileType(null); }} className="text-muted-foreground hover:text-foreground">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
                <div className="rounded-xl border border-border/60 bg-card shadow-sm focus-within:border-accent/50 focus-within:shadow-md transition-all">
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isFileParsing ? 'Processing attachment...' : (phase === 'input' ? 'Describe what you want to create...' : 'Ask for changes...')}
                    rows={phase === 'input' ? 2 : 1}
                    className="w-full text-sm bg-transparent px-3.5 pt-3 pb-1 resize-none focus:outline-none placeholder:text-muted-foreground/60 min-h-[44px] max-h-[120px]"
                    disabled={isLoading || isFileParsing}
                    autoFocus
                  />
                  <div className="flex items-center justify-between px-2 pb-2">
                    <div className="flex items-center gap-0.5">
                      <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp" onChange={handleFileSelect} />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        title="Attach file"
                      >
                        <Paperclip className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      onClick={handleInputSubmit}
                      disabled={!input.trim() || isLoading || isFileParsing}
                      className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center hover:opacity-80 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Workspace panel */}
        {isWorkspacePhase && workspaceData && (
          <div className="flex-1 overflow-hidden">
            {(() => {
              const { outline, questionnaireData, flowConfig } = workspaceData;
              const preloadedData = { outline, questionnaireData, flowConfig };
              const outputType = plan?.recommended_output_type || 'presentation';
              const commonProps = {
                onBack,
                onFullscreen,
                userId,
                existingProjectId,
                onComplete,
                onUpdate,
                externalChatMode: true,
                onChatMessage: (text) => setMessages(prev => [...prev, { role: 'bot', text }]),
                chatMessages: messages,
              };

              if (outputType === 'presentation') {
                return <CreatePPTWorkspace ref={workspaceRef} {...commonProps} preloadedOutline={preloadedData} />;
              }
              if (outputType === 'infographic') {
                return <InfographicWorkspace ref={workspaceRef} {...commonProps} preloadedData={preloadedData} />;
              }
              if (outputType === 'document') {
                return <DocumentWorkspace ref={workspaceRef} {...commonProps} preloadedData={preloadedData} />;
              }
              if (outputType === 'dashboard') {
                return <DashboardWorkspace ref={workspaceRef} {...commonProps} preloadedData={preloadedData} />;
              }
              if (outputType === 'sheets') {
                return <SheetsWorkspace ref={workspaceRef} {...commonProps} preloadedData={preloadedData} />;
              }
              return null;
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Reasoning Message with Typewriter ────────────────────────
function ReasoningMessage({ msg, reasoningOpen, setReasoningOpen }) {
  const isStreaming = msg.streaming;
  const isOpen = isStreaming || reasoningOpen;
  const hasText = msg.text && msg.text.length > 0;
  // Typewriter: 80 chars/sec while streaming, instant when done
  const displayedText = useTypewriter(msg.text || '', isStreaming ? 80 : 9999);
  const isTyping = isStreaming || displayedText.length < (msg.text || '').length;

  return (
    <div className="py-1">
      <button
        onClick={() => !isStreaming && setReasoningOpen(!reasoningOpen)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
      >
        {isStreaming ? (
          <div className="w-3 h-3 rounded-full border-[1.5px] border-accent/30 border-t-accent animate-spin" />
        ) : (
          <ChevronRight className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
        )}
        {isStreaming ? 'Thinking...' : 'Reasoning'}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="text-xs text-muted-foreground mt-1.5 pl-4 border-l-2 border-accent/30 leading-relaxed whitespace-pre-wrap">
              {hasText ? displayedText : <span className="italic opacity-60">Waiting for model response...</span>}
              {isTyping && <span className="inline-block w-1.5 h-3.5 bg-accent/60 ml-0.5 animate-pulse" />}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Chat Message Component ───────────────────────────────────
function ChatMessage({ msg, onBrandSelect }) {
  const [reasoningOpen, setReasoningOpen] = useState(false);

  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-accent/10 border border-accent/20 rounded-2xl rounded-br-md px-4 py-2.5">
          <p className="text-sm text-foreground whitespace-pre-wrap">{msg.text}</p>
          {msg.fileName && (
            <div className="flex items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
              <Paperclip className="w-3 h-3" />
              {msg.fileName}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (msg.role === 'thinking') {
    return (
      <div className="flex items-center gap-2 py-2">
        <div className="relative w-5 h-5">
          <div className="absolute inset-0 rounded-full border-2 border-accent/30 border-t-accent animate-spin" />
        </div>
        <span className="text-sm text-muted-foreground">{msg.text}</span>
      </div>
    );
  }

  if (msg.role === 'reasoning') {
    return <ReasoningMessage msg={msg} reasoningOpen={reasoningOpen} setReasoningOpen={setReasoningOpen} />;
  }

  if (msg.role === 'plan') {
    return <PlanCard plan={msg.plan} analysis={msg.analysis} />;
  }

  if (msg.role === 'brand-picker') {
    return (
      <div className="py-3 space-y-3">
        <p className="text-sm text-foreground">{msg.text}</p>
        <div className="flex flex-wrap gap-2">
          {msg.brands?.map(bi => (
            <button
              key={bi.id}
              onClick={() => onBrandSelect(bi.id)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card hover:border-primary/40 hover:bg-primary/5 transition-all text-sm"
            >
              <div
                className="w-4 h-4 rounded-full border border-border/50 flex-shrink-0"
                style={{ backgroundColor: bi.primary_color || '#6366F1' }}
              />
              <span className="font-medium text-foreground">{bi.name}</span>
            </button>
          ))}
          <button
            onClick={() => onBrandSelect('none')}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card hover:border-muted-foreground/40 transition-all text-sm text-muted-foreground"
          >
            No brand — skip
          </button>
        </div>
      </div>
    );
  }

  if (msg.brandPill) {
    return (
      <div className="flex items-center gap-2 py-1">
        <div
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: msg.brandPill.color || '#6366F1' }}
        />
        <span className="text-xs text-muted-foreground">
          Using brand: <span className="font-medium text-foreground">{msg.brandPill.name}</span>
        </span>
      </div>
    );
  }

  if (msg.role === 'outline') {
    const { outline, outputType } = msg;
    const typeMeta = OUTPUT_TYPE_META[outputType] || OUTPUT_TYPE_META.presentation;
    const isStreaming = outline.streaming;
    return (
      <div className="space-y-2 py-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-foreground mb-2">
          {isStreaming ? (
            <div className="w-3 h-3 rounded-full border-[1.5px] border-accent/30 border-t-accent animate-spin" />
          ) : null}
          {isStreaming
            ? `Building outline... (${outline.slides?.length} slides so far)`
            : `Outline — ${outline.slides?.length} sections for your ${typeMeta.label}`}
        </div>
        {outline.title && (
          <div className="text-sm font-medium text-foreground mb-2">{outline.title}</div>
        )}
        <div className="space-y-1.5">
          {outline.slides?.map((slide, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="flex items-start gap-2 bg-card border border-border/50 rounded-lg px-3 py-2.5"
            >
              <span className="flex-shrink-0 w-5 h-5 rounded-md bg-accent/10 text-accent text-[10px] font-semibold flex items-center justify-center mt-0.5">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-medium text-foreground">{slide.title}</p>
                  {slide.slide_type && (
                    <span className="flex-shrink-0 text-[9px] px-1.5 py-0.5 rounded-full bg-accent/10 text-accent font-medium">
                      {slide.slide_type.replace(/-/g, ' ')}
                    </span>
                  )}
                </div>
                {slide.content && <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{slide.content}</p>}
                {slide.impact && (
                  <p className="text-[10px] text-accent/70 mt-1 italic">{slide.impact}</p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  if (msg.role === 'error') {
    return (
      <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800/40 rounded-xl px-4 py-3">
        <p className="text-sm text-red-700 dark:text-red-300">{msg.text}</p>
      </div>
    );
  }

  if (msg.role === 'system') {
    return (
      <div className="flex justify-center">
        <span className="text-xs text-muted-foreground bg-muted/50 px-3 py-1 rounded-full">{msg.text}</span>
      </div>
    );
  }

  // bot message
  return (
    <div className="max-w-[80%]">
      <div className="text-sm text-foreground [&_p]:mb-1.5 [&_p:last-child]:mb-0 [&_ul]:mb-1.5 [&_ul]:ml-4 [&_ul]:list-disc [&_ol]:mb-1.5 [&_ol]:ml-4 [&_ol]:list-decimal [&_li]:mb-0.5 [&_strong]:font-semibold [&_em]:italic [&_code]:bg-muted [&_code]:px-1 [&_code]:rounded [&_code]:text-xs">
        <ReactMarkdown>{msg.text}</ReactMarkdown>
        {msg.isStreaming && <span className="inline-block w-1.5 h-4 bg-accent/60 ml-0.5 animate-pulse align-middle" />}
      </div>
    </div>
  );
}

// ─── Plan Card (inline in chat) ───────────────────────────────
function PlanCard({ plan, analysis }) {
  const typeMeta = OUTPUT_TYPE_META[plan.recommended_output_type] || OUTPUT_TYPE_META.presentation;

  return (
    <div className="space-y-3 py-2">
      {/* Output type badge */}
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-sm">
        <span>{typeMeta.icon}</span>
        <span className="font-medium text-foreground">{typeMeta.label}</span>
      </div>
      {plan.output_type_rationale && (
        <p className="text-xs text-muted-foreground">{plan.output_type_rationale}</p>
      )}

      {/* Strategic insight */}
      {plan.strategic_insight && (
        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 rounded-lg p-3 flex gap-2">
          <Lightbulb className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 dark:text-amber-300">{plan.strategic_insight}</p>
        </div>
      )}

      {/* Audience */}
      {plan.audience && (
        <div className="bg-card border border-border/50 rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Users className="w-3.5 h-3.5 text-accent" />
            <span className="text-xs font-semibold text-foreground">Audience</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div><span className="text-muted-foreground">Who</span><p className="text-foreground">{plan.audience.who}</p></div>
            <div><span className="text-muted-foreground">Level</span><p className="text-foreground">{plan.audience.knowledge_level}</p></div>
            <div><span className="text-muted-foreground">Outcome</span><p className="text-foreground">{plan.audience.desired_outcome}</p></div>
          </div>
        </div>
      )}

      {/* Purpose */}
      {plan.purpose && (
        <div className="bg-card border border-border/50 rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Target className="w-3.5 h-3.5 text-accent" />
            <span className="text-xs font-semibold text-foreground">Purpose</span>
          </div>
          <p className="text-xs text-foreground">{plan.purpose}</p>
        </div>
      )}

      {/* Content Strategy */}
      {plan.content_strategy && (
        <div className="bg-card border border-border/50 rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <BookOpen className="w-3.5 h-3.5 text-accent" />
            <span className="text-xs font-semibold text-foreground">Content Strategy</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'emphasize', label: 'Emphasize', color: 'text-green-600 dark:text-green-400' },
              { key: 'cut', label: 'Cut', color: 'text-red-600 dark:text-red-400' },
              { key: 'create', label: 'Create', color: 'text-blue-600 dark:text-blue-400' },
              { key: 'preserve', label: 'Preserve', color: 'text-amber-600 dark:text-amber-400' },
            ].map(({ key, label, color }) => {
              const items = plan.content_strategy[key] || [];
              if (items.length === 0) return null;
              return (
                <div key={key}>
                  <span className={`text-[10px] font-medium ${color}`}>{label}</span>
                  <ul className="mt-0.5 space-y-0.5">
                    {items.map((item, i) => <li key={i} className="text-[11px] text-foreground">• {item}</li>)}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Structure */}
      {plan.structure?.type_specific && Object.keys(plan.structure.type_specific).length > 0 && (
        <div className="bg-card border border-border/50 rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-accent" />
            <span className="text-xs font-semibold text-foreground">Structure</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {Object.entries(plan.structure.type_specific).map(([key, value]) => (
              <div key={key}>
                <span className="text-muted-foreground text-[10px]">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                <p className="text-foreground text-[11px]">
                  {Array.isArray(value) ? value.join(', ') : typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Visual Direction */}
      {plan.visual_direction && (
        <div className="bg-card border border-border/50 rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Palette className="w-3.5 h-3.5 text-accent" />
            <span className="text-xs font-semibold text-foreground">Design Direction</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {plan.visual_direction.style && (
              <div>
                <span className="text-muted-foreground text-[10px]">Style</span>
                <p className="text-foreground text-[11px]">{plan.visual_direction.style}</p>
              </div>
            )}
            {plan.visual_direction.mood && (
              <div>
                <span className="text-muted-foreground text-[10px]">Mood</span>
                <p className="text-foreground text-[11px]">{plan.visual_direction.mood}</p>
              </div>
            )}
            {plan.visual_direction.color_approach && (
              <div>
                <span className="text-muted-foreground text-[10px]">Colors</span>
                <p className="text-foreground text-[11px]">{plan.visual_direction.color_approach}</p>
              </div>
            )}
            {plan.visual_direction.density && (
              <div>
                <span className="text-muted-foreground text-[10px]">Tone</span>
                <p className="text-foreground text-[11px]">{plan.visual_direction.density}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Claude-style Question Overlay ────────────────────────────
function QuestionOverlay({ questions, onSubmit, onSkip }) {
  const [answers, setAnswers] = useState({});
  const [freeTextValues, setFreeTextValues] = useState({});

  const handleSelect = (qId, answer) => {
    setAnswers(prev => ({ ...prev, [qId]: answer }));
    setFreeTextValues(prev => ({ ...prev, [qId]: '' }));
  };

  const handleFreeText = (qId, value) => {
    setFreeTextValues(prev => ({ ...prev, [qId]: value }));
    if (value.trim()) {
      setAnswers(prev => ({ ...prev, [qId]: value.trim() }));
    }
  };

  const allAnswered = questions.every(q => answers[q.id]);

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 20, opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="border-t border-border/50 bg-card/95 backdrop-blur-sm"
    >
      <div className="px-4 py-4 max-h-[60vh] overflow-y-auto space-y-5">
        {questions.map((q, idx) => (
          <div key={q.id}>
            {/* Question header */}
            <div className="flex items-start gap-3 mb-3">
              <span className="flex-shrink-0 text-sm font-medium text-foreground">{q.question}</span>
            </div>
            {q.why_it_matters && (
              <p className="text-[11px] text-muted-foreground mb-3 ml-0">{q.why_it_matters}</p>
            )}

            {/* Answer options — numbered list like Claude */}
            <div className="space-y-1">
              {[...(q.suggested_answers || []), "Not sure — recommend the best approach"].map((ans, ansIdx) => {
                const isSelected = answers[q.id] === ans;
                const isNotSure = ansIdx === (q.suggested_answers?.length || 0);
                return (
                  <button
                    key={ans}
                    onClick={() => handleSelect(q.id, ans)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-all border ${
                      isSelected
                        ? 'bg-accent/10 border-accent/30 text-foreground'
                        : 'bg-transparent border-transparent hover:bg-muted/50 text-foreground'
                    } ${isNotSure ? 'text-muted-foreground' : ''}`}
                  >
                    <span className={`flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-xs font-medium ${
                      isSelected ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground'
                    }`}>
                      {ansIdx + 1}
                    </span>
                    <span>{ans}</span>
                  </button>
                );
              })}

              {/* Free text option */}
              {q.allow_free_text && (
                <div className="flex items-center gap-3 px-3 py-2">
                  <span className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center bg-muted">
                    <Pencil className="w-3 h-3 text-muted-foreground" />
                  </span>
                  <input
                    type="text"
                    placeholder="Something else..."
                    value={freeTextValues[q.id] || ''}
                    onChange={(e) => handleFreeText(q.id, e.target.value)}
                    className="flex-1 text-sm bg-transparent border-none outline-none text-foreground placeholder:text-muted-foreground"
                  />
                </div>
              )}
            </div>

            {idx < questions.length - 1 && <div className="border-b border-border/30 mt-4" />}
          </div>
        ))}
      </div>

      {/* Submit / Skip bar */}
      <div className="px-4 py-3 border-t border-border/30 flex items-center justify-between">
        <button onClick={onSkip} className="text-xs text-muted-foreground hover:text-foreground">
          Skip
        </button>
        <Button
          onClick={() => onSubmit(answers)}
          disabled={!allAnswered}
          size="sm"
          className="px-5"
        >
          Continue
        </Button>
      </div>
    </motion.div>
  );
}

// ─── Plan Editor ──────────────────────────────────────────────
function PlanEditor({ plan, onChange, onSave, onCancel, isRefining }) {
  const update = (path, value) => {
    const updated = JSON.parse(JSON.stringify(plan));
    const keys = path.split('.');
    let obj = updated;
    for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]] = obj[keys[i]] || {};
    obj[keys[keys.length - 1]] = value;
    onChange(updated);
  };

  return (
    <div className="space-y-3 py-2">
      {/* Output type */}
      <div className="bg-card border border-border/50 rounded-lg p-3">
        <label className="text-[10px] text-muted-foreground mb-2 block">Output Type</label>
        <div className="flex gap-1.5 flex-wrap">
          {Object.entries(OUTPUT_TYPE_META).filter(([type]) => SHOW_SHEETS || type !== 'sheets').map(([type, meta]) => (
            <button
              key={type}
              onClick={() => update('recommended_output_type', type)}
              className={`px-2.5 py-1 rounded-full text-xs border transition-all ${
                plan.recommended_output_type === type ? 'bg-accent text-accent-foreground border-accent' : 'bg-muted/50 border-border hover:border-accent/50'
              }`}
            >
              {meta.icon} {meta.label}
            </button>
          ))}
        </div>
      </div>

      {/* Purpose */}
      <div className="bg-card border border-border/50 rounded-lg p-3">
        <label className="text-[10px] text-muted-foreground mb-1 block">Purpose</label>
        <textarea
          value={plan.purpose || ''}
          onChange={(e) => update('purpose', e.target.value)}
          className="w-full px-2 py-1 rounded border border-border bg-background text-xs text-foreground resize-none focus:outline-none focus:ring-1 focus:ring-accent/50"
          rows={2}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2">
        <button onClick={onCancel} disabled={isRefining} className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
        <Button onClick={onSave} disabled={isRefining} size="sm">
          {isRefining ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Refining...</> : <><Check className="w-3 h-3 mr-1" />Save</>}
        </Button>
      </div>
    </div>
  );
}
